import { Link, match, useHistory } from 'react-router-dom'
import type { ColumnProps } from 'antd/lib/table'
import { Button, Card, Flex, Heading } from '@weareberyl/design-system'

import { prependScheme, PRODUCT_CREATE } from '~/constants/routes'
import Table from '~/components/Table'
import HeadTitle from '~/components/HeadTitle'
import { useQueryParams } from '~/utils/useQueryParams'

import {
  SchemeProductListItem,
  UseSchemeProducts,
  defaultData,
  useSchemeProducts,
} from './data'
import { ProductTypeTag } from '../components'
import { formatDuration, isBuyableProduct } from '../utils'
import { hasPermission } from '~/components/Session'
import { useCurrentUser } from '~/hooks'

const id = 'products-table'

type SchemeProductComponentProps = {
  setQueryParams: (params: { current?: number; pageSize?: number }) => void
  expandedProducts?: string
  schemeId: string
} & UseSchemeProducts

const SchemeProductComponent = ({
  setQueryParams,
  data,
  loading,
  schemeId,
}: SchemeProductComponentProps) => {
  const history = useHistory()
  const [user] = useCurrentUser()
  const columns: ColumnProps<SchemeProductListItem>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
    },
    {
      title: '',
      width: 50,
      render: ({
        is_protected,
        is_discoverable,
        fulfill_rewards,
      }: SchemeProductListItem) => {
        const icons: string[] = []

        if (is_protected) {
          icons.push('🔒')
        }
        if (fulfill_rewards.length > 0) {
          icons.push('⭐')
        }
        if (!is_discoverable) {
          icons.push('🎟️')
        }
        return <span>{icons.join(' ')}</span>
      },
    },
    {
      title: 'Name',
      width: 200,
      render: ({ name, is_active, id }: SchemeProductListItem) => {
        let title = <span>{name}</span>
        if (!is_active) {
          title = (
            <span>
              <s>{name}</s> (inactive)
            </span>
          )
        }

        return (
          <Link to={`products/${id}`}>
            <strong>{title}</strong>
          </Link>
        )
      },
    },
    {
      title: 'Product type',
      dataIndex: 'product_type',
      render: product_type => {
        return <ProductTypeTag productType={product_type} />
      },
    },
    {
      title: 'Price',
      dataIndex: ['price', 'formatted_amount'],
      render: (formattedAmount, product) => {
        if (!isBuyableProduct(product.product_type)) {
          return ''
        }
        return formattedAmount
      },
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      render: (quantity: string) => quantity.toLocaleString(),
    },
    {
      title: 'Valid for',
      dataIndex: 'validity_interval',
      render: validity_interval => formatDuration(validity_interval),
    },
  ]

  if (user && hasPermission(user.roles, 'product.edit', parseInt(schemeId))) {
    columns.push({
      title: 'Edit',
      dataIndex: 'id',
      render: id => {
        return <Link to={`products/${id}/edit`}>Edit</Link>
      },
    })
  }

  const tableData = data ?? defaultData

  return (
    <Table<SchemeProductListItem>
      id={id}
      onChange={({ current, pageSize }) =>
        setQueryParams({ current, pageSize })
      }
      columns={columns}
      rowClassName={({ is_active, is_discoverable }: SchemeProductListItem) => {
        if (!is_active) {
          return 'inactive-row'
        }
        if (!is_discoverable) {
          return 'subdued-row'
        }
        return ''
      }}
      rowKey={'id'}
      data={tableData}
      loading={loading}
      onRow={({ id }) => ({
        onDoubleClick() {
          history.push(`products/${id}`)
        },
      })}
    />
  )
}

type Props = {
  match: match<{ scheme_id: string }>
}

const ProductList = ({ match }: Props) => {
  const schemeId = match.params.scheme_id
  const history = useHistory()
  const [{ current, pageSize }, setQueryParams] = useQueryParams(id, {
    pageSize: 50,
  })
  const { data, loading } = useSchemeProducts({ current, pageSize, schemeId })

  return (
    <>
      <HeadTitle pageTitle="Products" />
      <Card p={5}>
        <Flex justifyContent="space-between" alignItems="flex-end" mb={5}>
          <Heading variant="callout">Products</Heading>
          <Button
            title="Create new product"
            onClick={() => {
              history.push(prependScheme(PRODUCT_CREATE, schemeId))
            }}
          />
        </Flex>
        <SchemeProductComponent
          setQueryParams={setQueryParams}
          data={data}
          loading={loading}
          schemeId={schemeId}
        />
      </Card>
    </>
  )
}

export default ProductList
